import {
  STORAGE_KEYS,
  clearUnauthorizedHandler,
  customFetch,
  registerUnauthorizedHandler,
} from '@agos/api-client'
import type {
  AuthMeResponse,
  AuthenticatedEmployee,
  LoginResponse,
  MyPermissionsResponse,
} from '@agos/api-client/model'

/**
 * The signed-in session as this app holds it: what the server answered, plus what the client knows.
 *
 * Typing the auth endpoints (BLOCKED #4) made the difference visible. Both extra fields were being
 * read off a bag that could hold anything, so nothing said where they came from:
 *
 * - `isSuperAdmin` is **set here**, on a Control Tower sign-in. No employee endpoint answers it.
 * - `preferredLanguage` **is answered since `BACKEND.md` §10A.95**, and this comment used to say the
 *   opposite twice over. It claimed the field was never answered *and* that "only `Customer` has the
 *   column" - and `employees.preferred_language` exists, which is where the profile screen has been
 *   writing it all along. So `setLanguage(user.preferredLanguage)` below read `undefined` on every
 *   sign-in, and the owner reported it as *"I changed it to HU and it was not changed"*. Keeping the
 *   line rather than deleting it is what made the fix one field on a response.
 *
 * `/auth/login` answers `SignedInEmployee`, which is narrower — the token's claims, without the
 * avatar or split name. Widening it here is deliberate: the session is the union of what has been
 * learned, and `/auth/me` fills the rest in on the next load.
 */
export interface SessionUser extends AuthenticatedEmployee {
  isSuperAdmin?: boolean
  // `preferredLanguage` is no longer redeclared here: §10A.95 put it on the generated
  // `AuthenticatedEmployee`, so this interface inherits it. It was declared locally only because the
  // endpoint did not answer it and the type had nowhere else to come from.
}
import { forgetSurface } from '../surfaces'
import { setLanguage } from '../i18n'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

/**
 * The signed-in employee, ported from legacy `contexts/auth-context.tsx`.
 *
 * Two things here are load-bearing and were ported deliberately rather than
 * tidied:
 *
 *   1. `hasPermission` returns true for `main_admin` without consulting the
 *      list. The backend grants that role everything, and the sidebar depends
 *      on the two agreeing.
 *   2. A 401 is not proof the session ended. The handler re-asks `/auth/me` and
 *      only signs out on an explicit 401/403 — a 5xx or a dropped connection is
 *      treated as transient. A kitchen tablet on flaky restaurant wifi would
 *      otherwise sign itself out mid-service.
 */

const PERMISSION_REFRESH_INTERVAL_MS = 3 * 60 * 1000

interface AuthContextValue {
  user: SessionUser | null
  token: string | null
  isLoading: boolean
  permissions: string[]
  hasPermission: (permission: string) => boolean
  login: (email: string, password: string) => Promise<void>
  loginSuperAdmin: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  refreshPermissions: () => Promise<void>
  refreshUser: () => Promise<void>
  /**
   * Adopt a session minted by something other than `login` — the first-run
   * setup form and the employee registration form both create an account and
   * are handed back a user and a token.
   */
  applySession: (employee: SessionUser, sessionToken: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function readStoredUser(): SessionUser | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user)
    return raw ? (JSON.parse(raw) as SessionUser) : null
  } catch {
    return null
  }
}

function readStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEYS.token)
  } catch {
    return null
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Render from the cached user immediately; `/auth/me` corrects it. Without
  // this the whole shell flashes a spinner on every reload.
  const [user, setUserState] = useState<SessionUser | null>(readStoredUser)
  const [token, setToken] = useState<string | null>(readStoredToken)
  // Derived, not set in the effect: with no stored token there is nothing to
  // verify and the shell can render the login screen on the first paint.
  const [isLoading, setIsLoading] = useState(() => readStoredToken() !== null)
  const [permissions, setPermissions] = useState<string[]>([])
  const signOutInProgress = useRef(false)

  const setUser = useCallback((nextUser: SessionUser | null) => {
    setUserState(nextUser)
    try {
      if (nextUser) {
        localStorage.setItem(STORAGE_KEYS.user, JSON.stringify(nextUser))
        if (nextUser.preferredLanguage) setLanguage(nextUser.preferredLanguage)
      } else {
        localStorage.removeItem(STORAGE_KEYS.user)
      }
    } catch {
      // A kiosk with storage disabled still works for this session.
    }
  }, [])

  const forgetSession = useCallback(() => {
    setUser(null)
    setToken(null)
    setPermissions([])
    try {
      localStorage.removeItem(STORAGE_KEYS.token)
      // §9.35 — `forgetSurface` was written for this line and never called from it. Signing out
      // cleared the token and the user and left the remembered surface behind, so on a shared tablet
      // the next person to sign in landed on the last person's screen. `surfaceFor` refuses a surface
      // the new role cannot use, which is what kept this from being visible — but two waiters sharing
      // a till have the same role, and one of them chose that surface.
      forgetSurface(localStorage)
    } catch {
      // nothing to forget
    }
  }, [setUser])

  const fetchPermissions = useCallback(async () => {
    try {
      const response = await customFetch<MyPermissionsResponse>('/api/v1/auth/my-permissions')
      setPermissions(response.permissions ?? [])
    } catch {
      // Keep the permissions already held: emptying them would collapse the
      // sidebar over one failed poll.
    }
  }, [])

  /**
   * What to do about a 401 seen anywhere in the app. Ported semantics: verify
   * before believing it.
   */
  const handleUnauthorized = useCallback(async () => {
    if (signOutInProgress.current) return
    signOutInProgress.current = true

    try {
      if (!readStoredToken()) {
        forgetSession()
        return
      }
      await customFetch<unknown>('/api/v1/auth/me')
      // The session is alive; the 401 belonged to one endpoint.
      return
    } catch (error) {
      const status = (error as { status?: number }).status
      if (status === 401 || status === 403) {
        forgetSession()
      }
      // Anything else — 5xx, network — is transient. Stay signed in.
    } finally {
      signOutInProgress.current = false
    }
  }, [forgetSession])

  useEffect(() => {
    registerUnauthorizedHandler(handleUnauthorized)
    return () => clearUnauthorizedHandler()
  }, [handleUnauthorized])

  // One verification on mount.
  useEffect(() => {
    const storedToken = readStoredToken()
    if (!storedToken) return

    let cancelled = false
    void (async () => {
      try {
        const response = await customFetch<AuthMeResponse>('/api/v1/auth/me')
        if (cancelled) return
        setUser(response.user ?? null)
        setToken(storedToken)
        await fetchPermissions()
      } catch (error) {
        if (cancelled) return
        const status = (error as { status?: number }).status
        if (status === 401 || status === 403) forgetSession()
        else setToken(storedToken)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [fetchPermissions, forgetSession, setUser])

  // A role's permissions can be edited while somebody is signed in, and the
  // sidebar has to notice. Legacy polls every three minutes and on refocus.
  useEffect(() => {
    if (!token) return

    const poll = setInterval(() => void fetchPermissions(), PERMISSION_REFRESH_INTERVAL_MS)
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void fetchPermissions()
    }
    document.addEventListener('visibilitychange', refreshWhenVisible)
    window.addEventListener('focus', refreshWhenVisible)

    return () => {
      clearInterval(poll)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
      window.removeEventListener('focus', refreshWhenVisible)
    }
  }, [token, fetchPermissions])

  const applySession = useCallback(
    (employee: SessionUser, sessionToken: string) => {
      setUser(employee)
      setToken(sessionToken)
      try {
        localStorage.setItem(STORAGE_KEYS.token, sessionToken)
      } catch {
        // session-only sign-in
      }
      void fetchPermissions()
    },
    [fetchPermissions, setUser],
  )

  const login = useCallback(
    async (email: string, password: string) => {
      const session = await customFetch<LoginResponse>('/api/v1/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      setUser(session.user ?? null)
      setToken(session.token ?? null)
      try {
        localStorage.setItem(STORAGE_KEYS.token, session.token ?? '')
      } catch {
        // session-only sign-in
      }
      await fetchPermissions()
    },
    [fetchPermissions, setUser],
  )

  /**
   * Control Tower sign-in. The Tower-signed token is stored under the same key
   * so every request carries it, and `isSuperAdmin` is set locally because the
   * franchise switcher renders from it without waiting for `/auth/me`.
   */
  const loginSuperAdmin = useCallback(
    async (email: string, password: string) => {
      const session = await customFetch<LoginResponse>('/api/v1/central/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      })
      setUser({ ...session.user, isSuperAdmin: true })
      setToken(session.token ?? null)
      try {
        localStorage.setItem(STORAGE_KEYS.token, session.token ?? '')
      } catch {
        // session-only sign-in
      }
      setPermissions([])
    },
    [setUser],
  )

  const logout = useCallback(async () => {
    try {
      await customFetch<unknown>('/api/v1/auth/logout', { method: 'POST' })
    } catch {
      // Sign out locally even when the server cannot be told.
    }
    forgetSession()
    try {
      localStorage.removeItem(STORAGE_KEYS.franchiseId)
    } catch {
      // nothing to forget
    }
  }, [forgetSession])

  const hasPermission = useCallback(
    (permission: string) => {
      if (user?.systemRole === 'main_admin') return true
      return permissions.includes(permission)
    },
    [user, permissions],
  )

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isLoading,
      permissions,
      hasPermission,
      login,
      loginSuperAdmin,
      logout,
      applySession,
      refreshPermissions: fetchPermissions,
      refreshUser: async () => {
        try {
          const response = await customFetch<AuthMeResponse>('/api/v1/auth/me')
          setUser(response.user ?? null)
        } catch {
          // best effort; the cached user is still good
        }
      },
    }),
    [
      user,
      token,
      isLoading,
      permissions,
      hasPermission,
      login,
      loginSuperAdmin,
      logout,
      applySession,
      fetchPermissions,
      setUser,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside AuthProvider')
  return context
}
